import type { Dispatch, RefObject, SetStateAction } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

function measureOverflow(node: HTMLElement) {
  return {
    width: node.scrollWidth > node.offsetWidth,
    height: node.scrollHeight > node.offsetHeight,
  };
}

function publishOverflow(
  node: HTMLElement | null,
  setIsOverflowing: Dispatch<SetStateAction<{ width: boolean; height: boolean }>>,
) {
  if (!node) {
    return;
  }

  const next = measureOverflow(node);
  setIsOverflowing((current) => (current.width === next.width && current.height === next.height ? current : next));
}

export function useIsOverflowing<T extends HTMLElement>(providedRef?: RefObject<T | null>) {
  const defaultRef = useRef<T>(null);
  const ref = providedRef || defaultRef;
  const [isOverflowing, setIsOverflowing] = useState({
    height: false,
    width: false,
  });

  useEffect(() => {
    publishOverflow(ref.current, setIsOverflowing);
  }, [ref]);

  useLayoutEffect(() => {
    const onResize = () => {
      publishOverflow(ref.current, setIsOverflowing);
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [ref]);

  const checkOverflow = () => {
    publishOverflow(ref.current, setIsOverflowing);
  };

  return { ref, isOverflowing, checkOverflow };
}
